import _ from 'underscore';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { applyPartialConversionColumnName } from '@bingads-webui-campaign/column-conversions';

import { Constants } from '../constants';
import { getShoppingGridColumnConfig } from './shopping-grid-column-config';
import { columnConfigToColumnChooserConfig, isConversionsColumnAllowed } from '../column-config-to-chooser-config';
import { updateSovColumns } from '../shared/util';

export function getShoppingGridConfig({
  pageContext,
  i18n,
  jsDataCampaign,
  edm,
  shoppingSchema,
  getCurrentLevel,
  bingadsGridColumn,
  globalConfig,
  gridType,
  isForVNext,
}) {
  const permissions = pageContext.Permissions;
  const {
    IsViewThroughConversionEnabled,
    IsAvgPositionDeprecationPhase1Enabled,
    IsManualCpmEnabled,
    // IsViewThroughConversionsRevenueEnabled,
  } = permissions;
  // (youngim) MT bug needs to be fixed for dimensinos
  const IsViewThroughConversionsRevenueEnabled = false;
  const shoppingColumnConfig = getShoppingGridColumnConfig({
    i18n, bingadsGridColumn, getCurrentLevel, globalConfig,
  });

  function lambdaConvertForLanguages(logic) {
    return (values) => {
      let url = '';

      _.each(values, (value, index) => {
        url += `contains(Language, '${value}')`;
        if (index !== values.length - 1) {
          url += ` ${logic} `;
        }
      });
      return url;
    };
  }

  return {
    getColumns: () => shoppingColumnConfig.getLevelOfDetailColumns()
      .concat(shoppingColumnConfig.getPerfColumns())
      .concat(shoppingColumnConfig.getConversionColumns()),
    getColumnChooserConfiguration: (logger) => {
      const level = getCurrentLevel(logger);
      const lockedColumns = [];

      switch (gridType) {
        case Constants.ShoppingCategory:
          lockedColumns.push('CategoryL1');
          break;
        case Constants.ShoppingProductType:
          lockedColumns.push('ProductType1');
          break;
        case Constants.ShoppingBrand:
          lockedColumns.push('Brand');
          break;
        case Constants.ShoppingItem:
          lockedColumns.push('MerchantIdentifier');
          break;
        case Constants.ShoppingStore:
          lockedColumns.push('ProviderId');
          break;
        default:
          break;
      }
      lockedColumns.push('Impressions');

      if (logger) {
        logger.trace(`Current level: ${level}`, 'Dimension/load');
      }

      let defaultColumns = ['Impressions', 'Clicks', 'CTR', 'AverageCPC', 'Spend', 'Conversions'];

      if (IsAvgPositionDeprecationPhase1Enabled) {
        defaultColumns = ['AbsoluteTopImpressionRatePercent'].concat(defaultColumns);
      } else {
        defaultColumns = ['AveragePosition'].concat(defaultColumns);
      }

      switch (gridType) {
        case Constants.ShoppingCategory:
          defaultColumns = ['CategoryL2', 'CategoryL3'].concat(defaultColumns);
          break;
        case Constants.ShoppingProductType:
          defaultColumns = ['ProductType2', 'ProductType3'].concat(defaultColumns);
          break;
        case Constants.ShoppingBrand:
          break;
        case Constants.ShoppingItem:
          break;
        case Constants.ShoppingStore:
          break;
        default:
          break;
      }
      const columnChooserConfig = {
        categories: [{
          name: i18n.getString('LevelOfDetail'),
          options: columnConfigToColumnChooserConfig(
            shoppingColumnConfig.getLevelOfDetailColumns(),
            lockedColumns
          ),
        }, {
          name: i18n.getString('Performance'),
          options: columnConfigToColumnChooserConfig(
            shoppingColumnConfig.getPerfColumns(),
            lockedColumns
          ),
        }, {
          name: i18n.getString('Conversions'),
          options: columnConfigToColumnChooserConfig(
            _.filter(
              shoppingColumnConfig.getConversionColumns(),
              ({ name }) => isConversionsColumnAllowed({
                name,
                IsViewThroughConversionEnabled,
                IsViewThroughConversionsRevenueEnabled,
              })
            ),
            lockedColumns
          ),
        }],
        selected: lockedColumns.concat(defaultColumns)
          .concat(_.compact([
            IsManualCpmEnabled && 'AverageCPM',
          ])),
        mandatory: lockedColumns,
        locked: lockedColumns,
        isForVNext,
        patchQuery: true,
      };

      if (logger) {
        logger.trace(`Current selected columns: ${columnChooserConfig.selected}`, 'Dimension/load');
      }

      if (isForVNext) {
        columnChooserConfig.customToolbarIcon = 'ba-TripleColumnSolid';
      }

      return columnChooserConfig;
    },

    getEntity: () => jsDataCampaign.Time,

    getEntitySet: () => _.result(edm.currentAccount, 'Time'),

    getSchema: () => {
      const level = getCurrentLevel();

      const columns = ['CampaignName', 'AdGroupName'];
      const performanceMetricsProperty = shoppingSchema.properties.PerformanceMetrics;
      const allConvMetrics = applyPartialConversionColumnName(permissions, [
        'AllConversionCPA',
        'AllConversions',
        'AllConversionRate',
        'AllConversionAdvertiserReportedRevenue',
        'AllConversionRevenueOnAdSpend',
        'ViewThroughConversions',
        'ViewThroughConversionsRevenue',
        'ViewThroughConversionsCPA',
        'ViewThroughConversionsReturnOnAdSpend',
        'ViewThroughConversionsRate',
      ]);

      _.each(allConvMetrics, (allConvMetric) => {
        if (performanceMetricsProperty &&
            putil.get(performanceMetricsProperty.schema.properties, allConvMetric)) {
          shoppingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
            .schema.properties[allConvMetric].filterable = true;
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversions')) {
            shoppingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsRevenue')) {
            shoppingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsCPA')) {
            shoppingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsReturnOnAdSpend')) {
            shoppingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsRate')) {
            shoppingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
        }
      });

      // remove AverageCPM since we don't support this in V1
      delete shoppingSchema.properties.PerformanceMetrics.schema.properties.AverageCPM; // eslint-disable-line no-param-reassign, max-len

      _.each(Object.keys(shoppingSchema.properties), (propertyKey) => {
        if (columns.indexOf(propertyKey) > -1) {
          delete shoppingSchema.properties[propertyKey]; // eslint-disable-line no-param-reassign
        }
      });

      if (level === Constants.AccountLevel) {
        shoppingSchema.properties.CampaignName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
        shoppingSchema.properties.AdGroupName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
      } else if (level === Constants.CampaignLevel) {
        shoppingSchema.properties.AdGroupName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
      }

      const sovColumnNames = {
        AbsoluteTopImpressionRate: 'AbsoluteTopImpressionRatePercent',
      };

      updateSovColumns(shoppingSchema, sovColumnNames);

      return shoppingSchema;
    },
    getReportName: () => {
      let reportType = null;

      switch (gridType) {
        case Constants.ShoppingCategory:
          reportType = 'ShoppingCategory';
          break;
        case Constants.ShoppingProductType:
          reportType = 'ShoppingProductType';
          break;
        case Constants.ShoppingBrand:
          reportType = 'ShoppingBrand';
          break;
        case Constants.ShoppingItem:
          reportType = 'ShoppingItemId';
          break;
        case Constants.ShoppingStore:
          reportType = 'ShoppingStoreId';
          break;
        default:
          break;
      }
      return reportType;
    },
    getReportSchema() {
      return _.defaults({
        name: this.getReportName(gridType),
      }, this.getSchema(gridType));
    },
    modifyFilter: (filters) => {
      const language = filters.fields.filter(item => item.name === 'Language')[0];

      if (language) {
        language.title = i18n.getString('ColumnName_Language');
        language.type = 'multi-language';
        language.operatorDisplayNameMap = {
          containsany: i18n.getString('FilterOperator_ContainsAny'),
        };
        language.operators = ['containsany'];
        language.addtionalParams = {
          items: [{
            Id: 'EN',
            Name: i18n.getString('Dimension_Lang_EN'),
          }, {
            Id: 'DA',
            Name: i18n.getString('Dimension_Lang_DA'),
          }, {
            Id: 'NL',
            Name: i18n.getString('Dimension_Lang_NL'),
          }, {
            Id: 'FI',
            Name: i18n.getString('Dimension_Lang_FI'),
          }, {
            Id: 'TH',
            Name: i18n.getString('Dimension_Lang_TH'),
          }, {
            Id: 'VI',
            Name: i18n.getString('Dimension_Lang_VI'),
          }, {
            Id: 'FR',
            Name: i18n.getString('Dimension_Lang_FR'),
          }, {
            Id: 'DE',
            Name: i18n.getString('Dimension_Lang_DE'),
          }, {
            Id: 'IT',
            Name: i18n.getString('Dimension_Lang_IT'),
          }, {
            Id: 'NB',
            Name: i18n.getString('Dimension_Lang_NO'),
          }, {
            Id: 'PT',
            Name: i18n.getString('Dimension_Lang_PT'),
          }, {
            Id: 'ES',
            Name: i18n.getString('Dimension_Lang_ES'),
          }, {
            Id: 'SV',
            Name: i18n.getString('Dimension_Lang_SV'),
          }, {
            Id: 'ZH-HANT',
            Name: i18n.getString('Dimension_Lang_ZH_HANT'),
          }],
        };
      }
      return filters;
    },
    getOdataOptions: () => {
      const rtnObj = {
        odata: {
          custom: {},
        },
      };

      _.extendOwn(rtnObj.odata.custom, {
        Language: {
          containsany: lambdaConvertForLanguages('or'),
        },
      });
      return rtnObj;
    },
    getRewriteMap: () => ({
      CategoryL1: i18n.getString('ColumnName_CategoryL1'),
      CategoryL2: i18n.getString('ColumnName_CategoryL2'),
      CategoryL3: i18n.getString('ColumnName_CategoryL3'),
      CategoryL4: i18n.getString('ColumnName_CategoryL4'),
      CategoryL5: i18n.getString('ColumnName_CategoryL5'),
      ProviderId: i18n.getString('ColumnName_ProviderId'),
      Condition: i18n.getString('ColumnName_Condition'),
      Title: i18n.getString('ColumnName_Title'),
      Language: i18n.getString('ColumnName_Language'),
      MerchantIdentifier: i18n.getString('DimensionDropdown_ItemId'),
      AdGroupName: i18n.getString('FilterLocalization_DataAttribute_AdGroupName'),
      CampaignName: i18n.getString('FilterLocalization_DataAttribute_CampaignName'),
      ProductType1: i18n.getString('ColumnName_ProductType1'),
      ProductType2: i18n.getString('ColumnName_ProductType2'),
      ProductType3: i18n.getString('ColumnName_ProductType3'),
      ProductType4: i18n.getString('ColumnName_ProductType4'),
      ProductType5: i18n.getString('ColumnName_ProductType5'),
      CustomLabel0: i18n.getString('ColumnName_CustomLabel0'),
      CustomLabel1: i18n.getString('ColumnName_CustomLabel1'),
      CustomLabel2: i18n.getString('ColumnName_CustomLabel2'),
      CustomLabel3: i18n.getString('ColumnName_CustomLabel3'),
      CustomLabel4: i18n.getString('ColumnName_CustomLabel4'),
    }),
  };
}
